import {useEffect,useState} from "react";
import {getJson} from "./api";
import {SectionStatus} from "./AgentPanel";
import type {IntegrationCenterStatus,OnboardingState,OnboardingStatus,Session} from "./types";

const stateLabel:Record<OnboardingState,string>={
 complete:"Listo",action_required:"Configurar",external_required:"Conexión pendiente",blocked:"Bloqueado",not_applicable:"No aplica",
};
const stateClass:Record<OnboardingState,string>={
 complete:"enabled",action_required:"attention",external_required:"attention",blocked:"locked",not_applicable:"locked",
};
const roadmapLabel:Record<IntegrationCenterStatus["catalog"][number]["roadmapState"],string>={
 adapter_prepared:"Adaptador preparado",planned:"Planificado",
 partner_discovery:"Acceso partner",evaluation:"En evaluación",
};

export default function OnboardingPanel({session,onNavigate}:{session:Session;onNavigate:(section:string)=>void}){
 const [value,setValue]=useState<OnboardingStatus>();
 const [integrations,setIntegrations]=useState<IntegrationCenterStatus>();
 const [state,setState]=useState<"loading"|"ready"|"error">("loading");
 const load=()=>{setState("loading");Promise.all([
  getJson<OnboardingStatus>(session,"/v1/onboarding/status"),
  getJson<IntegrationCenterStatus>(session,"/v1/integrations"),
 ]).then(([next,nextIntegrations])=>{
  setValue(next);setIntegrations(nextIntegrations);setState("ready");
 }).catch(()=>setState("error"));};
 useEffect(load,[session.token,session.tenantId]);
 if(state!=="ready"||!value||!integrations)return <SectionStatus state={state==="error"?"error":"loading"} retry={load}/>;

 return <div className="workspace"><header className="workspace-head"><div>
  <span className="eyebrow">Puesta en marcha</span><h1>Prepara tu agente paso a paso</h1>
  <p>La plataforma comprueba la configuración real y separa lo que puedes completar aquí de las conexiones externas.</p>
 </div><span className={value.readyForTest?"readonly-chip setup-ready":"readonly-chip"}>
  {value.readyForTest?"Preparado para prueba":"Configuración en curso"}</span></header>

 <section className="panel onboarding-progress"><div className="progress-copy">
  <div><span className="eyebrow">Progreso</span><strong>{value.progress.percent}%</strong></div>
  <p>{value.progress.completed} de {value.progress.total} pasos aplicables están completos.</p>
 </div><div className="progress-track" aria-label={"Progreso "+value.progress.percent+"%"}>
  <span style={{width:value.progress.percent+"%"}}/></div>
 </section>

 <section className="onboarding-grid">
  <article className="panel onboarding-steps"><div className="panel-title"><div>
   <span className="eyebrow">Checklist</span><h2>Activación del negocio</h2></div></div>
   <div className="setup-list">{value.steps.map((item,index)=><div className={"setup-row state-"+item.state} key={item.id}>
    <span className="setup-index">{item.state==="complete"?"V":index+1}</span>
    <div className="setup-copy"><b>{item.label}</b><small>{item.description}</small></div>
    <span className={"status "+stateClass[item.state]}>{stateLabel[item.state]}</span>
    {item.target&&item.state!=="complete"&&item.state!=="not_applicable"&&
     <button className="secondary" onClick={()=>onNavigate(item.target!)}>Abrir</button>}
   </div>)}</div>
  </article>

  <aside className="panel connection-panel"><div className="panel-title"><div>
   <span className="eyebrow">Conexiones</span><h2>Servicios externos</h2></div></div>
   <div className="connection-list">
    <Connection name="Telefonía y voz" status={value.connections.voice.status} provider={value.connections.voice.provider}/>
    <Connection name="Calendario" status={value.connections.calendar.status} provider={value.connections.calendar.provider}/>
   </div>
   <div className="safety-note"><b>Sin falsos positivos</b><p>
    Un servicio solo aparece conectado cuando existe evidencia técnica. Las integraciones pendientes no se simulan como activas.
   </p></div>
   <div className="activation-card"><small>Activación real</small><b>{value.readyForActivation?"Disponible":"Todavía bloqueada"}</b>
    <p>Antes de tráfico real deben completarse telefonía, identidad, staging y los gates externos correspondientes.</p></div>
  </aside>
 </section>

 <IntegrationFabricPanel value={integrations}/>
 </div>;
}

function Connection({name,status,provider}:{name:string;status:string;provider:string|null}){
 const connected=status==="connected"||status==="connected_local";
 const label=status==="not_applicable"?"No aplica":connected?"Conectado":"Pendiente";
 return <div className="connection-row"><span className={connected?"live-dot":"muted-dot"}/><div><b>{name}</b>
  <small>{provider?provider.toUpperCase():"Sin proveedor activo"}</small></div><span className={"status "+(connected?"enabled":"locked")}>{label}</span></div>;
}

function IntegrationFabricPanel({value}:{value:IntegrationCenterStatus}){
 const actual=new Map(value.connections.map(item=>[item.provider,item]));
 const p1=value.catalog.filter(item=>item.priority==="P1");
 const p2=value.catalog.filter(item=>item.priority==="P2");
 return <section className="panel integration-center">
  <div className="panel-title"><div><span className="eyebrow">Integration Fabric</span><h2>Conecta MIL con tu forma de trabajar</h2>
   <p className="muted">El catálogo muestra la hoja de ruta. Solo las capacidades verificadas pueden aparecer como activas.</p></div>
   <span className="readonly-chip">FASE 5E · solo sintético</span></div>
  <div className="integration-list">{p1.map(item=><IntegrationRoadmapRow key={item.provider} item={item} actual={actual.get(item.provider)}/>)}</div>
  <details className="integration-more"><summary>Ver integraciones posteriores</summary>
   <div className="integration-list">{p2.map(item=><IntegrationRoadmapRow key={item.provider} item={item} actual={actual.get(item.provider)}/>)}</div>
  </details>
  <div className="safety-note"><b>Capacidades verificables</b><p>
   En este momento las conexiones de Integration Fabric no tienen credenciales ni acciones externas habilitadas. Un enlace de reserva nunca se mostrará como lectura o escritura de agenda.
  </p></div>
 </section>;
}

function IntegrationRoadmapRow({item,actual}:{
 item:IntegrationCenterStatus["catalog"][number];
 actual:IntegrationCenterStatus["connections"][number]|undefined;
}){
 const mode=actual?"Sintético P0":roadmapLabel[item.roadmapState];
 const detail=actual
  ? actual.capabilities.length+" capacidades externas · acciones externas "+(actual.externalActionsEnabled?"habilitadas":"bloqueadas")
  : item.fallback==="link_only"?"Fallback previsto: enlace de reserva":"Sin conexión activa";
 return <div className="integration-row">
  <span className="muted-dot"/><div><b>{item.label}</b><small>{item.domain} · {detail}</small></div>
  <span className="integration-priority">{item.priority}</span><span className="status locked">{mode}</span>
 </div>;
}
